import { observer } from 'owa-mobx-react';
import React from 'react';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import DragAndDroppable from 'owa-dnd/lib/components/DragAndDroppable';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { DRAG_X_OFFSET, DRAG_Y_OFFSET } from 'owa-mail-folder-view';
import { dragPreview } from './FavoriteNode.scss';
import {
    canDropFavorite,
    onDropFavorite,
    canDropFolder,
    onDropFolder,
} from '../util/dragDropUtils';
import {
    selectFolder,
    lazySelectPersona,
    lazySelectGroup,
    lazySelectFavoriteCategory,
} from 'owa-mail-folder-forest-actions';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import {
    type FolderForestNodeType,
    type OutlookFavoriteKind,
    type FavoriteNodeDragData,
} from 'owa-favorites-types';
import { getCustomBundledIcon } from 'owa-folders-common/lib/util/getCustomIconComponent';
import {
    bundleIcon,
    FolderRegular,
    FolderPersonRegular,
    ArchiveRegular,
    PeopleCommunityFilled,
    PeopleCommunityRegular,
    PersonFilled,
    PersonRegular,
    TagFilled,
    TagRegular,
} from '@fluentui/react-icons';
import type { FluentIcon } from '@fluentui/react-icons';
import type { MailboxInfo } from 'owa-client-types';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { getAnchorForContextMenu } from 'owa-positioning';
import { showFavoritesContextMenu } from 'owa-mail-favorites-store/lib/actions/favoritesContextMenu';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type { ActionSource } from 'owa-mail-store';

const PeopleCommunity = bundleIcon(PeopleCommunityFilled, PeopleCommunityRegular);
const Person = bundleIcon(PersonFilled, PersonRegular);
const Tag = bundleIcon(TagFilled, TagRegular);

export interface FavoritePlaceholderNodeProps {
    favoriteId: string;
    id: string; // id  used for selection and/or click handling
    type: OutlookFavoriteKind;
    mailboxInfo: MailboxInfo;
    displayName?: string;
    isVisible?: boolean;
    actionSource?: ActionSource;
}

const defaultExport: React.FunctionComponent<FavoritePlaceholderNodeProps> = observer(
    function FavoritePlaceholderNode(props: FavoritePlaceholderNodeProps) {
        const isSelected = useComputedValue((): boolean => {
            const selectedNode = getSelectedNode();
            if (props.type === 'persona') {
                return props.favoriteId === selectedNode?.id;
            }
            return props.id === selectedNode?.id;
        }, [props.id, props.type, props.favoriteId]);

        const icon = useComputedValue((): FluentIcon => {
            return getPlaceholderIcon(props.type, props.id);
        }, [props.type, props.id]);

        const forestNodeType = useComputedValue((): FolderForestNodeType => {
            return getForestNodeType(props.type);
        }, [props.type]);

        const onClick = React.useCallback(
            (evt: React.MouseEvent<EventTarget>) => {
                evt.stopPropagation();
                switch (props.type) {
                    case 'group':
                        lazySelectGroup.importAndExecute(props.id, 'favorites', props.mailboxInfo);
                        break;
                    case 'category':
                        lazySelectFavoriteCategory.importAndExecute(props.id);
                        break;
                    case 'persona':
                        lazySelectPersona.importAndExecute(props.favoriteId);
                        break;
                    default:
                        selectFolder(props.id, 'favorites', 'FavoritePlaceholderNodeClick');
                        break;
                }
            },
            [props.type, props.mailboxInfo, props.id]
        );

        const onContextMenu = React.useCallback(
            (evt: React.MouseEvent<HTMLElement>) => {
                evt.stopPropagation();
                evt.preventDefault();

                showFavoritesContextMenu(
                    props.favoriteId,
                    forestNodeType,
                    getAnchorForContextMenu(evt),
                    undefined /*targetFolderId*/,
                    undefined /*showRootMenu*/,
                    true /*isPlaceholderNode*/,
                    props.actionSource
                );
            },
            [props.favoriteId, props.actionSource]
        );

        const getDragData = React.useCallback(() => {
            const dragData: FavoriteNodeDragData = {
                itemType: DraggableItemTypes.FavoriteNode,
                favoriteId: props.favoriteId,
                favoriteType: forestNodeType,
                displayName: props.displayName ?? '',
                itemData: {
                    mailboxInfo: props.mailboxInfo,
                },
            };
            return dragData;
        }, [props.favoriteId, props.displayName, props.mailboxInfo]);

        const canDrop = React.useCallback(
            (dragData: DragData) => {
                switch (dragData.itemType) {
                    case DraggableItemTypes.FavoriteNode:
                        return canDropFavorite(dragData, props.mailboxInfo);
                    case DraggableItemTypes.MailFolderNode:
                        return canDropFolder(dragData, props.mailboxInfo);
                    default:
                        return false;
                }
            },
            [props.mailboxInfo]
        );

        const onDrop = React.useCallback(
            async (dragData: DragData) => {
                const itemType = dragData.itemType;
                switch (itemType) {
                    case DraggableItemTypes.MailFolderNode:
                        await onDropFolder(
                            dragData,
                            props.mailboxInfo,
                            props.favoriteId,
                            props.actionSource
                        );
                        break;
                    case DraggableItemTypes.FavoriteNode:
                        await onDropFavorite(dragData, props.favoriteId, props.actionSource);
                        break;
                }
            },
            [props.mailboxInfo, props.favoriteId]
        );

        if (!props.isVisible || !props.id || !props.displayName) {
            return null;
        }

        // If the favorite is a public folder from an account that is not the primary
        if (
            props.type === 'publicFolder' &&
            props.mailboxInfo != getGlobalSettingsAccountMailboxInfo()
        ) {
            return null;
        }

        const dropViewState = createDropViewState();

        return (
            <DragAndDroppable
                getDragData={getDragData}
                getDragPreview={getDragPreview}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}
                dropViewState={dropViewState}
                onDrop={onDrop}
                canDrop={canDrop}
            >
                <TreeNode
                    displayName={props.displayName ?? ''}
                    customIconComponent={icon}
                    isRootNode={false}
                    key={props.id}
                    isFavorited={true}
                    isSelected={isSelected}
                    isDisabled={false}
                    isDroppedOver={dropViewState.isDragOver}
                    ellipsesOnHover={true}
                    showAsHoverOnDroppedOver={false}
                    shouldShowFocusBorder={true}
                    onClick={onClick}
                    onContextMenu={onContextMenu}
                    useNewSelectionUI={isGlobalFeatureEnabled('fp-newfoldersselection-ui')}
                />
            </DragAndDroppable>
        );
    },
    'FavoritePlaceholderNode'
);
export default defaultExport;

function getDragPreview(dragData: DragData) {
    const elem = document.createElement('div');
    elem.className = dragPreview;
    elem.innerText = (dragData as FavoriteNodeDragData)?.displayName ?? '';
    return elem;
}

function getPlaceholderIcon(type: OutlookFavoriteKind, id: string): FluentIcon {
    switch (type) {
        case 'group':
            return PeopleCommunity;
        case 'category':
            return Tag;
        case 'persona':
            return Person;
        case 'localFolderArchive':
            return ArchiveRegular;
        case 'localFolderShared':
            return FolderPersonRegular;
        default:
            return getCustomBundledIcon(id, true /* showNonSpecialFolderIcon */) ?? FolderRegular;
    }
}

function getForestNodeType(type: OutlookFavoriteKind): FolderForestNodeType {
    switch (type) {
        case 'group':
            return 2;
        case 'category':
            return 4;
        case 'persona':
            return 1;
        case 'publicFolder':
            return 5;
        default:
            return 0;
    }
}
